import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Eye, CheckCircle2, XCircle, RefreshCw, Users, Mail, AlertCircle, Zap, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

function StatBadge({ children, tone = "neutral" }) {
  const tones = {
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    bad: "bg-red-50 text-red-700 border-red-200",
    neutral: "bg-neutral-50 text-neutral-700 border-neutral-200",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] font-medium ${tones[tone]}`}>{children}</span>;
}

export default function AdminNewsletterCompose() {
  const [smtp, setSmtp] = useState(null);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [testEmail, setTestEmail] = useState("thomas@dickenbrok.me");
  const [sendingTest, setSendingTest] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [active, setActive] = useState(null); // active campaign detail (with progress)
  const pollRef = useRef(null);

  const loadStatus = async () => {
    try { const { data } = await api.get("/newsletter/smtp-status"); setSmtp(data); }
    catch { toast.error("Konnte SMTP-Status nicht laden."); }
  };
  const loadCampaigns = async () => {
    try { const { data } = await api.get("/newsletter/campaigns"); setCampaigns(data || []); }
    catch { /* noop */ }
  };

  useEffect(() => { loadStatus(); loadCampaigns(); }, []);

  // Poll active campaign every 2s while it's not completed.
  useEffect(() => {
    if (!active?.id || active.status === "completed" || active.status === "failed") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/newsletter/campaigns/${active.id}`);
        setActive(data);
        if (data.status === "completed") { loadCampaigns(); }
      } catch { /* noop */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [active?.id, active?.status]);

  const sendTest = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("Bitte Betreff und Inhalt eintragen."); return; }
    if (!testEmail.trim()) { toast.error("Bitte Test-Empfänger eintragen."); return; }
    setSendingTest(true);
    try {
      await api.post("/newsletter/campaigns/test", { to: testEmail.trim(), subject, html });
      toast.success(`Testmail an ${testEmail} verschickt.`);
    } catch (e) {
      toast.error("Testmail fehlgeschlagen: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally { setSendingTest(false); }
  };

  const dispatch = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("Bitte Betreff und Inhalt eintragen."); return; }
    if (!smtp?.recipient_count) { toast.error("Keine bestätigten Empfänger vorhanden."); return; }
    const rc = smtp.recipient_count;
    if (!window.confirm(`Newsletter an ${rc} bestätigte${rc === 1 ? "n Abonnenten" : " Abonnenten"} versenden? Der Versand läuft in Batches von 10 Mails im Hintergrund.`)) return;
    setDispatching(true);
    try {
      const { data } = await api.post("/newsletter/campaigns", { subject, html });
      toast.success(`Versand gestartet — Kampagne ${data.id.slice(0, 8)}…`);
      const { data: detail } = await api.get(`/newsletter/campaigns/${data.id}`);
      setActive(detail);
      loadCampaigns();
    } catch (e) {
      toast.error("Versand fehlgeschlagen: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally { setDispatching(false); }
  };

  const smtpTone = smtp?.ok ? "ok" : smtp?.configured ? "bad" : "warn";

  return (
    <div className="space-y-8" data-testid="admin-newsletter-compose">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/admin/newsletter" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-neutral-500 hover:text-neutral-800 mb-2">
            <ArrowLeft className="h-3.5 w-3.5" /> Abonnenten
          </Link>
          <h1 className="text-3xl font-semibold text-neutral-900">Newsletter versenden</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">
            Verschickt an <strong>bestätigte</strong> Abonnenten (Double-Opt-In). Batches von 10 Mails im Hintergrund, mit Live-Fortschritt.
          </p>
        </div>
        <button onClick={() => { loadStatus(); loadCampaigns(); }} className="inline-flex items-center gap-1.5 border border-brand-line px-3 py-1.5 text-xs hover:bg-neutral-50" data-testid="newsletter-reload"><RefreshCw className="h-3.5 w-3.5" /> Aktualisieren</button>
      </div>

      {/* SMTP + audience status */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className={`border p-4 ${smtp?.ok ? "bg-emerald-50/40 border-emerald-200" : "bg-red-50/40 border-red-200"}`} data-testid="newsletter-smtp-card">
          <div className="flex items-start gap-2">
            {smtp?.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500">SMTP</div>
              <div className="text-sm font-semibold text-neutral-900">{smtp?.ok ? "Verbindung OK" : "Nicht erreichbar"}</div>
              <div className="text-xs text-neutral-500 truncate">{smtp?.host}:{smtp?.port}</div>
              <div className="text-[11px] text-neutral-500 truncate">{smtp?.user}</div>
              {smtp?.error && <div className="text-[11px] text-red-700 mt-1 break-all">{smtp.error}</div>}
            </div>
          </div>
        </div>
        <div className="border border-brand-line bg-white p-4" data-testid="newsletter-doi-card">
          <div className="flex items-start gap-2">
            <CheckCircle2 className={`h-4 w-4 shrink-0 mt-0.5 ${smtp?.double_opt_in ? "text-emerald-600" : "text-neutral-400"}`} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500">Double-Opt-In</div>
              <div className="text-sm font-semibold text-neutral-900">{smtp?.double_opt_in ? "Aktiv" : "Nicht aktiv"}</div>
              <div className="text-xs text-neutral-500">Bestätigungslink pro Anmeldung</div>
            </div>
          </div>
        </div>
        <div className="border border-brand-line bg-white p-4" data-testid="newsletter-recipients-card">
          <div className="flex items-start gap-2">
            <Users className="h-4 w-4 text-neutral-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500">Bestätigte Empfänger</div>
              <div className="text-2xl font-semibold text-neutral-900 leading-none mt-0.5">{smtp?.recipient_count ?? "—"}</div>
              <div className="text-xs text-neutral-500 mt-1">{smtp?.total_subscribers ?? 0} gesamt · {smtp?.pending_confirmations ?? 0} unbestätigt</div>
            </div>
          </div>
        </div>
        <div className="border border-brand-line bg-white p-4">
          <div className="flex items-start gap-2">
            <Mail className="h-4 w-4 text-neutral-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500">Batch-Größe</div>
              <div className="text-2xl font-semibold text-neutral-900 leading-none mt-0.5">10</div>
              <div className="text-xs text-neutral-500 mt-1">2 s Pause / Batch · 0,25 s / Mail</div>
            </div>
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className="border border-brand-line bg-white" data-testid="newsletter-composer">
        <div className="px-6 py-4 border-b border-brand-line">
          <h2 className="text-sm font-semibold text-neutral-900">Nachricht</h2>
        </div>
        <div className="p-6 space-y-5">
          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Betreff *</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="newsletter-subject-input"
              placeholder='z. B. "Baufortschritt Februar 2026 — Baugrube Nord fertig"'
              className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))]"
            />
          </label>
          <div>
            <span className="block text-xs text-neutral-500 mb-1.5">Inhalt (HTML)</span>
            <RichTextEditor value={html} onChange={setHtml} minHeight={280} testid="newsletter-html-editor" />
            <p className="text-[11px] text-neutral-500 mt-1.5">Wird automatisch in den Detmold-Rahmen (Logo, Fuß, Abmeldelink) eingebettet.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-neutral-500">Test an
              <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="newsletter-test-email" className="border border-brand-line px-2 py-1.5 text-sm w-64" />
            </label>
            <button onClick={sendTest} disabled={sendingTest} data-testid="newsletter-send-test-btn" className="inline-flex items-center gap-2 border border-brand-line px-3 py-2 text-sm hover:bg-neutral-50 disabled:opacity-50">
              <Eye className="h-4 w-4" /> {sendingTest ? "Sende…" : "Testmail senden"}
            </button>
            <div className="grow" />
            <button
              onClick={dispatch}
              disabled={dispatching || !smtp?.recipient_count || (active && active.status === "sending")}
              data-testid="newsletter-dispatch-btn"
              className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-strong))] text-white px-4 py-2.5 text-sm hover:opacity-90 disabled:opacity-40"
            >
              <Send className="h-4 w-4" /> An {smtp?.recipient_count ?? 0} Abonnenten senden
            </button>
          </div>
        </div>
      </div>

      {/* Active dispatch progress */}
      {active && (
        <div className="border border-brand-line bg-white" data-testid="newsletter-progress">
          <div className="px-6 py-4 border-b border-brand-line flex items-center gap-3">
            {active.status === "sending" && <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--brand-green-strong))]" />}
            {active.status === "completed" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-neutral-900 truncate">{active.subject}</div>
              <div className="text-xs text-neutral-500">
                {active.status === "sending" ? "Versand läuft" : active.status === "completed" ? "Abgeschlossen" : active.status}
                {" · "}{fmtDateTime(active.started_at || active.created_at)}
              </div>
            </div>
            <button onClick={() => setActive(null)} className="text-xs text-neutral-400 hover:text-neutral-700">Schließen</button>
          </div>
          <div className="p-6">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-3xl font-semibold text-neutral-900">
                {active.progress?.done ?? 0}<span className="text-neutral-400 text-lg"> / {active.progress?.total ?? active.total_recipients ?? 0}</span>
              </div>
              <div className="text-xs text-neutral-500">
                Batch {active.progress?.batch ?? 0} / {active.progress?.total_batches ?? 0}
              </div>
            </div>
            <div className="w-full h-2 bg-neutral-100 overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--brand-green-strong))] transition-all duration-500"
                style={{ width: `${(active.progress?.total ? (active.progress.done / active.progress.total) * 100 : 0)}%` }}
              />
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm">
              <StatBadge tone="ok"><CheckCircle2 className="h-3 w-3" /> {active.sent_count ?? 0} versendet</StatBadge>
              {(active.failed_count ?? 0) > 0 && <StatBadge tone="bad"><AlertCircle className="h-3 w-3" /> {active.failed_count} fehlgeschlagen</StatBadge>}
            </div>
            {Array.isArray(active.log) && active.log.length > 0 && (
              <details className="mt-4 border-t border-brand-line pt-3">
                <summary className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-800">Protokoll ({active.log.length} Einträge)</summary>
                <div className="mt-3 max-h-64 overflow-y-auto text-xs divide-y divide-brand-line">
                  {active.log.slice().reverse().map((l, i) => (
                    <div key={i} className="py-1.5 flex items-center gap-2">
                      {l.status === "sent" ? <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" /> : <XCircle className="h-3 w-3 text-red-600 shrink-0" />}
                      <span className="font-mono text-[11px] text-neutral-500">{fmtDateTime(l.ts)}</span>
                      <span className="text-neutral-700 truncate">{l.email}</span>
                      {l.error && <span className="text-red-600 text-[11px] truncate flex-1">{l.error}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Past campaigns */}
      <div className="border border-brand-line bg-white" data-testid="newsletter-campaign-history">
        <div className="px-6 py-4 border-b border-brand-line">
          <h2 className="text-sm font-semibold text-neutral-900">Frühere Versandläufe</h2>
        </div>
        {campaigns.length === 0 ? (
          <div className="px-6 py-8 text-sm text-neutral-500 text-center">Noch keine Kampagnen versendet.</div>
        ) : (
          <div className="divide-y divide-brand-line">
            {campaigns.map((c) => (
              <button
                key={c.id}
                onClick={async () => {
                  const { data } = await api.get(`/newsletter/campaigns/${c.id}`);
                  setActive(data);
                }}
                data-testid={`newsletter-history-${c.id}`}
                className="w-full text-left px-6 py-3 hover:bg-neutral-50 flex items-center gap-4"
              >
                {c.status === "completed" ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : c.status === "sending" ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400 shrink-0" /> : <Zap className="h-4 w-4 text-amber-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">{c.subject}</div>
                  <div className="text-xs text-neutral-500">{fmtDateTime(c.created_at)}</div>
                </div>
                <div className="text-xs text-neutral-600 shrink-0">
                  <span className="text-emerald-700 font-semibold">{c.sent_count ?? 0}</span>
                  {c.failed_count > 0 && <> · <span className="text-red-600">{c.failed_count} ✗</span></>}
                  <span className="text-neutral-400"> / {c.total_recipients ?? 0}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
